import { useCallback, useEffect, useMemo, useState } from "react";
import { ContractDocument } from "screens/ContractSearch/ContractSearchTypes";
import {
  CONTRACT_DOCUMENTS_PAGE_SIZE,
  fetchContractDocuments,
} from "screens/ContractSearch/ContractSearchService";
import { pickableContracts } from "./ContractCompareHelper";

/** `skipped`: the plan or role blocks the page, so the list was never asked for (it must not read as "no contracts"). */
export type LibraryStatus = "loading" | "ready" | "failed" | "skipped";

/**
 * The user's own PDF and Word contracts, for the old-version picker. The first page shows at once and the rest
 * loads behind it (D46); a failure on a later page keeps what already came. `enabled: false` (the plan or role
 * blocks the page) skips the call but still leaves the loading state, so a gated page never shows a skeleton forever.
 */
export const useContractLibrary = (enabled: boolean) => {
  const [status, setStatus] = useState<LibraryStatus>("loading");
  const [documents, setDocuments] = useState<ContractDocument[]>([]);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setStatus("skipped");
      return undefined;
    }
    let cancelled = false;
    setStatus("loading");

    const load = async () => {
      try {
        const first = await fetchContractDocuments(CONTRACT_DOCUMENTS_PAGE_SIZE, 0);
        if (cancelled) return;
        let loaded = first.documents ?? [];
        setDocuments(loaded);
        setStatus("ready");

        let hasMore = !!first.hasMore && loaded.length > 0;
        while (hasMore && !cancelled) {
          const page = await fetchContractDocuments(CONTRACT_DOCUMENTS_PAGE_SIZE, loaded.length);
          if (cancelled) return;
          const rows = page.documents ?? [];
          if (!rows.length) break;
          loaded = [...loaded, ...rows];
          setDocuments(loaded);
          hasMore = !!page.hasMore;
        }
      } catch {
        // Only the first page decides: a later page that fails keeps the rows already shown.
        if (!cancelled) setStatus((current) => (current === "ready" ? current : "failed"));
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  const contracts = useMemo(() => pickableContracts([...documents]), [documents]);

  return { status, contracts, retry };
};
